import { useSyncExternalStore } from "react";

/** The ways down the side, or a bar across the top. */
export type Arrangement = "sidebar" | "bar";

/**
 * A window opens in the sidebar, because a desktop window has the room. The
 * top bar is the other arrangement, not the resting one.
 */
const RESTING: Arrangement = "sidebar";

/**
 * Where the choice is kept. Read during the first render rather than in an
 * effect, so the frame paints in the arrangement it was left in instead of
 * arriving in one and correcting itself.
 */
const storageKey = "pax-arrangement";

const subscribers = new Set<() => void>();

function readArrangement(): Arrangement {
  return localStorage.getItem(storageKey) === "bar" ? "bar" : RESTING;
}

export function chooseArrangement(arrangement: Arrangement) {
  if (arrangement === RESTING) localStorage.removeItem(storageKey);
  else localStorage.setItem(storageKey, arrangement);
  for (const notify of subscribers) notify();
}

function subscribe(notify: () => void) {
  subscribers.add(notify);
  const followOtherTabs = (event: StorageEvent) => {
    if (event.key === storageKey) notify();
  };
  window.addEventListener("storage", followOtherTabs);
  return () => {
    subscribers.delete(notify);
    window.removeEventListener("storage", followOtherTabs);
  };
}

export function useArrangement() {
  return useSyncExternalStore(subscribe, readArrangement, () => RESTING);
}
